import React, { useEffect, useMemo, useState } from "react";
import {
  TextArea,
  Input,
  Switch,
  Slider,
  SliderFill,
  SliderThumb,
  SliderTrack,
  Spinner,
} from "@heroui/react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { AgentConfigInput } from "@webuild-ai/admin/models/components";
import { LLMModel } from "@webuild-ai/agent-platform/models/components";
import { useTranslation } from "react-i18next";

import { useWorkflowStore } from "@/stores/WorkflowStore";
import { recordToolToggle } from "@/utils/chat/toolPreferences";
import { useChatStore } from "@/stores/ChatStore";
import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { useAvailableToolsState } from "@/hooks/playground/useAvailableTools";
import { logger } from "@/telemetry";
import { MarkdownWeBuild } from "@/components/MarkdownWeBuild";

import { ToolPicker } from "./ToolPicker";

interface WorkflowSettingsProps {
  isOpen: boolean;
}

const MODEL_GROUP_KEYS = new Set(["model", "temperature", "max_tokens"]);
const SYSTEM_MESSAGE_KEY = "system_message";

interface SettingsSectionProps {
  title: string;
  defaultOpen?: boolean;
  dataId?: string;
  children: React.ReactNode;
}

interface SystemMessageEditorProps {
  value: string;
  onChange: (val: string) => void;
  isDisabled: boolean;
}

const SystemMessageEditor: React.FC<SystemMessageEditorProps> = ({
  value,
  onChange,
  isDisabled,
}) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<"edit" | "preview">("preview");

  return (
    <div className="flex flex-col gap-2">
      <div
        aria-label={t("Toggle system message view")}
        className="flex bg-default-100 p-0.5 rounded-lg self-end"
        role="tablist"
      >
        {(["edit", "preview"] as const).map((m) => (
          <button
            key={m}
            aria-selected={mode === m}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-default-400 ${
              mode === m
                ? "bg-background text-foreground shadow-sm"
                : "text-default-500 hover:text-default-700"
            }`}
            role="tab"
            type="button"
            onClick={() => setMode(m)}
          >
            {m === "edit" ? t("Edit") : t("Preview")}
          </button>
        ))}
      </div>
      {mode === "edit" ? (
        <TextArea
          className="w-full min-h-125"
          data-id="param-system_message-textarea"
          isDisabled={isDisabled}
          rows={18}
          size="sm"
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <div
          className="w-full min-h-125 rounded-lg border border-default-200 bg-default-50 px-4 py-3 overflow-y-auto"
          data-id="param-system_message-preview"
        >
          <MarkdownWeBuild>{value}</MarkdownWeBuild>
        </div>
      )}
    </div>
  );
};

const SettingsSection: React.FC<SettingsSectionProps> = ({
  title,
  defaultOpen = false,
  dataId,
  children,
}) => (
  <details
    className="group/section border-b border-default-200 last:border-b-0"
    data-id={dataId}
    {...(defaultOpen ? { open: true } : {})}
  >
    <summary className="list-none cursor-pointer [&::-webkit-details-marker]:hidden">
      <div className="flex items-center gap-2 py-3">
        <span className="text-sm font-medium flex-1">{title}</span>
        <Icon
          className="text-default-400 transition-transform group-open/section:rotate-180"
          icon="solar:alt-arrow-down-linear"
          width={16}
        />
      </div>
    </summary>
    <div className="pb-4 pt-1 flex flex-col gap-4">{children}</div>
  </details>
);

export const WorkflowSettings: React.FC<WorkflowSettingsProps> = ({ isOpen }) => {
  const { workflow, setWorkflow, updateAgentTools } = useWorkflowStore();
  const { runId } = useChatStore();
  const { t } = useTranslation();

  const { platformClient } = useWebuildAPIClient();

  const [models, setModels] = useState<LLMModel[]>([]);
  const [isLoadingModels, setIsLoadingModels] = useState(true);
  // The tool registry is read through the shared hook, so opening this panel no
  // longer re-fetches a catalogue the canvas and the skills editor have very
  // likely already read.
  const { tools: available_tools, isLoading: isLoadingTools } = useAvailableToolsState();
  const isLoading = isLoadingModels || isLoadingTools;

  useEffect(() => {
    if (!platformClient) {
      logger.error("Platform client not available — skipping model fetch");
      setIsLoadingModels(false);

      return;
    }
    const fetchModels = async () => {
      try {
        setModels(await platformClient.models.list());
      } catch (error) {
        logger.error("Failed to fetch models", error);
      } finally {
        setIsLoadingModels(false);
      }
    };

    setIsLoadingModels(true);
    fetchModels();
  }, [platformClient, isOpen]);

  const updateAgentParameter = (agent_id: string, param: string, value: string) => {
    if (!workflow) return;

    const updatedWorkflow = {
      ...workflow,
      agents: {
        ...workflow.agents,
        [agent_id]: {
          ...workflow.agents[agent_id],
          parameters: {
            ...workflow.agents[agent_id].parameters,
            [param]: value,
          },
        },
      },
    };

    setWorkflow(updatedWorkflow);
  };

  const updateAgentParameters = (agent_id: string, paramsToUpdate: Record<string, unknown>) => {
    if (!workflow) return;

    const updatedWorkflow = {
      ...workflow,
      agents: {
        ...workflow.agents,
        [agent_id]: {
          ...workflow.agents[agent_id],
          parameters: {
            ...workflow.agents[agent_id].parameters,
            ...paramsToUpdate,
          },
        },
      },
    };

    setWorkflow(updatedWorkflow);
  };

  const renderSingleParameter = (
    agent_id: string,
    agent: AgentConfigInput,
    param: string,
    value: unknown,
    index: number,
    hideLabel = false,
  ) => {
    const label = param.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
    const uniqueKey = `${agent_id}-param-${param}-${index}`;
    const labelNode = hideLabel ? null : <label className="text-sm font-medium">{label}</label>;

    if (param === "temperature") {
      const parsedValue = parseFloat(String(value));

      return (
        <div key={uniqueKey} className="flex flex-col gap-2 w-full">
          <div className="flex items-center justify-between">
            {labelNode}
            <span className="text-sm tabular-nums text-default-500">
              {Number.isNaN(parsedValue) ? "0.7" : parsedValue.toFixed(1)}
            </span>
          </div>
          <Slider
            className="w-full"
            data-id={`param-${param}-slider`}
            defaultValue={Number.isNaN(parsedValue) ? 0.7 : parsedValue}
            isDisabled={!!runId}
            maxValue={1}
            minValue={0}
            step={0.1}
            onChange={(val) =>
              updateAgentParameter(agent_id, param, String(Array.isArray(val) ? val[0] : val))
            }
          >
            <SliderTrack>
              <SliderFill />
              <SliderThumb />
            </SliderTrack>
          </Slider>
        </div>
      );
    }

    if (param === "model") {
      const modelExists = models.some((m) => m.id === value);
      const isDisabled = !!runId || models.length === 0;

      return (
        <div key={uniqueKey} className="flex flex-col gap-2 w-full">
          {labelNode}
          <select
            className="w-full text-sm border border-default-200 rounded-lg px-3 py-2 bg-background"
            data-id={`param-${param}-select`}
            disabled={isDisabled}
            value={value && modelExists ? String(value) : ""}
            onChange={(e) => {
              const modelId = e.target.value;

              if (modelId) {
                const model = models.find((m) => m.id === modelId);

                updateAgentParameters(agent_id, {
                  model: modelId,
                  max_tokens: model?.maxTokens || 200000,
                });
              }
            }}
          >
            <option value="">
              {models.length === 0 ? t("Loading models...") : t("Select a model")}
            </option>
            {models.map((model) => (
              <option key={model.id} value={model.id}>
                {model.name}
              </option>
            ))}
          </select>
        </div>
      );
    }

    if (param === "max_tokens") {
      const parsedValue = parseInt(String(value));
      const model = models.find((m) => m.id === agent.parameters.model);
      const maxValue = model ? model.maxTokens : 200000;
      const displayValue = Number.isNaN(parsedValue) ? 0 : parsedValue;

      return (
        <div key={uniqueKey} className="flex flex-col gap-2 w-full">
          <div className="flex items-center justify-between">
            {labelNode}
            <span className="text-sm tabular-nums text-default-500">
              {displayValue.toLocaleString()}
            </span>
          </div>
          <Slider
            className="w-full"
            data-id={`param-${param}-slider`}
            isDisabled={!!runId}
            maxValue={maxValue}
            minValue={0}
            step={1000}
            value={displayValue}
            onChange={(val) =>
              updateAgentParameter(agent_id, param, String(Array.isArray(val) ? val[0] : val))
            }
          >
            <SliderTrack>
              <SliderFill />
              <SliderThumb />
            </SliderTrack>
          </Slider>
        </div>
      );
    }

    if (
      typeof value === "boolean" ||
      (typeof value === "string" &&
        (value.toLowerCase() === "true" || value.toLowerCase() === "false"))
    ) {
      return (
        <Switch
          key={uniqueKey}
          aria-label={label}
          className="pt-2"
          data-id={`param-${param}-switch`}
          defaultSelected={String(value).toLowerCase() === "true"}
          id={`${agent_id}-${param}`}
          isDisabled={!!runId}
          size="sm"
          onChange={(checked) => updateAgentParameter(agent_id, param, String(checked))}
        >
          <Switch.Content>
            <Switch.Control>
              <Switch.Thumb />
            </Switch.Control>
            {label}
          </Switch.Content>
        </Switch>
      );
    }

    if (typeof value === "string") {
      if (value.length > 20) {
        return (
          <div key={uniqueKey} className="flex flex-col gap-2 w-full">
            {labelNode}
            <TextArea
              className="w-full min-h-125"
              data-id={`param-${param}-textarea`}
              isDisabled={!!runId}
              rows={18}
              size="sm"
              type="text"
              value={value}
              onChange={(e) => updateAgentParameter(agent_id, param, e.target.value)}
            />
          </div>
        );
      }

      return (
        <div
          key={uniqueKey}
          className="flex flex-col gap-2 w-full"
          data-id={`param-${param}-container`}
        >
          {labelNode}
          <Input
            className="w-full"
            data-id={`param-${param}-input`}
            isDisabled={!!runId}
            type="text"
            value={value}
            onChange={(e) => updateAgentParameter(agent_id, param, e.target.value)}
          />
        </div>
      );
    }

    if (typeof value === "number") {
      return (
        <div key={uniqueKey} className="flex flex-col gap-2 w-full">
          <div className="flex items-center justify-between">
            {labelNode}
            <span className="text-sm tabular-nums text-default-500">{value.toLocaleString()}</span>
          </div>
          <Slider
            className="w-full"
            data-id={`param-${param}-slider`}
            defaultValue={value}
            isDisabled={!!runId}
            maxValue={5000}
            minValue={0}
            step={1}
            onChange={(val) =>
              updateAgentParameter(agent_id, param, String(Array.isArray(val) ? val[0] : val))
            }
          >
            <SliderTrack>
              <SliderFill />
              <SliderThumb />
            </SliderTrack>
          </Slider>
        </div>
      );
    }

    return null;
  };

  const renderFilteredParameters = (
    agent_id: string,
    agent: AgentConfigInput,
    include: (param: string) => boolean,
    hideLabels = false,
  ) => {
    const rendered = Object.entries(agent.parameters)
      .map(([param, value], index) => {
        if (!include(param)) return null;

        return renderSingleParameter(agent_id, agent, param, value, index, hideLabels);
      })
      .filter(Boolean);

    if (rendered.length === 0) return null;

    return <>{rendered}</>;
  };

  // Resolve chat agent entry for User Memory toggle
  const chatAgentEntry = useMemo(() => {
    if (!workflow?.agents) return null;

    return (
      Object.entries(workflow.agents).find(([_, agent]) => agent.type === "chat_agent") ?? null
    );
  }, [workflow]);

  const isMemoryEnabled = useMemo(() => {
    if (!chatAgentEntry) return false;
    const tools: string[] = chatAgentEntry[1]?.parameters?.tools ?? [];

    return tools.includes("save_to_memory");
  }, [chatAgentEntry]);

  const handleMemoryToggle = (val: boolean) => {
    if (!chatAgentEntry || !workflow) return;
    const [agentId] = chatAgentEntry;

    updateAgentTools(agentId, "save_to_memory", val);

    // Record only the memory decision, as a delta from the shared definition.
    recordToolToggle(workflow.id, agentId, "save_to_memory", val);
  };

  if (isLoading) {
    return (
      <div
        aria-label={t("Loading workflow settings")}
        aria-live="polite"
        className="flex w-full items-center justify-center p-4"
        role="status"
      >
        <Spinner />
      </div>
    );
  }

  if (!workflow) {
    return null;
  }

  return (
    <div className="w-full flex flex-col pt-2" data-id="workflow-settings">
      {Object.entries(workflow.agents).map(([agent_id, agent]) => {
        const paramKeys = Object.keys(agent.parameters);
        const hasModelGroup = paramKeys.some((k) => MODEL_GROUP_KEYS.has(k));
        const hasSystemMessage = paramKeys.includes(SYSTEM_MESSAGE_KEY);
        // Only include params whose value type renderSingleParameter knows how
        // to render — skips arrays/objects like `tools` so "Advanced" doesn't
        // appear as an empty section.
        const otherKeys = paramKeys.filter((k) => {
          if (MODEL_GROUP_KEYS.has(k) || k === SYSTEM_MESSAGE_KEY) return false;
          const v = agent.parameters[k];

          return typeof v === "boolean" || typeof v === "string" || typeof v === "number";
        });

        return (
          <div key={agent_id} className="flex flex-col" data-id={`agent-${agent_id}`}>
            <h3 className="text-base text-primary-700 pb-1">
              {agent_id.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())}
            </h3>

            <SettingsSection
              defaultOpen
              dataId={`agent-${agent_id}-tools-section`}
              title={t("Tools")}
            >
              <ToolPicker
                agent={agent}
                agentId={agent_id}
                availableTools={available_tools}
                isDisabled={!!runId}
              />
            </SettingsSection>

            {hasModelGroup && (
              <SettingsSection dataId={`agent-${agent_id}-model-section`} title={t("Model")}>
                {renderFilteredParameters(agent_id, agent, (p) => MODEL_GROUP_KEYS.has(p))}
              </SettingsSection>
            )}

            {hasSystemMessage && (
              <SettingsSection
                dataId={`agent-${agent_id}-system-message-section`}
                title={t("System Message")}
              >
                <SystemMessageEditor
                  isDisabled={!!runId}
                  value={String(agent.parameters.system_message ?? "")}
                  onChange={(val) => updateAgentParameter(agent_id, SYSTEM_MESSAGE_KEY, val)}
                />
              </SettingsSection>
            )}

            {otherKeys.length > 0 && (
              <SettingsSection dataId={`agent-${agent_id}-advanced-section`} title={t("Advanced")}>
                {renderFilteredParameters(agent_id, agent, (p) => otherKeys.includes(p))}
              </SettingsSection>
            )}
          </div>
        );
      })}

      {chatAgentEntry && (
        <SettingsSection dataId="user-memory-section" title={t("Memory")}>
          <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-default-200 hover:bg-default-50 transition-colors">
            <div className="w-7 h-7 flex items-center justify-center rounded-md bg-default-100 border border-default-200 shrink-0">
              {/* Black `currentColor` glyph in an <img>; invert it in dark mode. */}
              <img
                alt={t("Memory")}
                className="w-4 h-4 object-contain dark:invert"
                src="/brands/mem0-icon.svg"
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">{t("User Memory")}</div>
              <div className="text-[11px] text-default-400">
                {t("Remember facts about you for personalised responses")}
              </div>
            </div>
            <Switch
              aria-label={t("Toggle User Memory")}
              data-id="user-memory-toggle"
              isDisabled={!!runId}
              isSelected={isMemoryEnabled}
              size="sm"
              onChange={handleMemoryToggle}
            >
              <Switch.Content>
                <Switch.Control>
                  <Switch.Thumb />
                </Switch.Control>
              </Switch.Content>
            </Switch>
          </div>
        </SettingsSection>
      )}
    </div>
  );
};
